import { ThoughtsAndSpeakUpsResponseInterface } from '@api/thoughtsAndSpeakUpsApiHandler';
import { Close as CloseIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import { Box, Fade, IconButton, Paper, Slide, Snackbar, Typography } from '@mui/material';
import React from 'react';

export interface NotificationComponentProps {
  open: boolean;
  notification: ThoughtsAndSpeakUpsResponseInterface | null;
  onClose: () => void;
  onClick?: () => void;
  position?: {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  };
  autoHideDuration?: number | null;
  showCloseButton?: boolean;
  clickable?: boolean;
  customColors?: {
    background?: string;
    text?: string;
    icon?: string;
  };
  noDataMessage?: {
    title?: string;
    subtitle?: string;
  };
  className?: string;
  style?: React.CSSProperties;
}

const NotificationComponent: React.FC<NotificationComponentProps> = ({
  open,
  notification,
  onClose,
  onClick,
  position = { vertical: 'top', horizontal: 'center' },
  autoHideDuration = null,
  showCloseButton = true,
  clickable = true,
  customColors = {
    background: '#0072b0',
    text: 'white',
    icon: 'white',
  },
  noDataMessage = {
    title: 'All Caught Up!',
    subtitle: "You don't have any new messages at the moment.",
  },
  className,
  style,
}) => {
  const handleClick = () => {
    if (clickable && notification && onClick) {
      onClick();
    }
  };

  const handleCloseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  const getTypeIcon = () => (
    // if (!notification?.type)
    //   return <NotificationsIcon sx={{ color: customColors.icon, fontSize: '20px' }} />;

    // switch (notification.type) {
    //   case 'success':
    //     return <NotificationsIcon sx={{ color: customColors.icon, fontSize: '20px' }} />;
    //   case 'warning':
    //     return <NotificationsIcon sx={{ color: customColors.icon, fontSize: '20px' }} />;
    //   case 'error':
    //     return <NotificationsIcon sx={{ color: customColors.icon, fontSize: '20px' }} />;
    //   default:
    <NotificationsIcon sx={{ color: customColors.icon, fontSize: '20px' }} />
  );

  const getBackgroundColor = () => '#0072b0';

  const formatTimestamp = (timestamp?: Date | string) => {
    if (!timestamp) return '';
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    const isYesterday =
      new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString() === date.toDateString();

    const timeString = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    if (isToday) {
      return `Today | ${timeString}`;
    }

    if (isYesterday) {
      return `Yesterday | ${timeString}`;
    }

    const dateString = date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

    return `${dateString} | ${timeString}`;
  };

  return (
    <Snackbar
      open={open}
      onClose={onClose}
      autoHideDuration={autoHideDuration}
      TransitionComponent={Slide}
      anchorOrigin={position}
      sx={{
        mt: { xs: 7, sm: 8 },
        mx: { xs: 1, sm: 0 },
        ml: position.horizontal === 'left' ? { xs: 0, sm: 2 } : 0,
        mr: position.horizontal === 'right' ? { xs: 0, sm: 2 } : 0,
        '& .MuiSnackbar-root': {
          position: 'fixed',
        },
        zIndex: 9999,
        '@media (max-width: 768px)': {
          left: position.horizontal === 'center' ? '48% !important' : 'auto',
          right: 'auto !important',
          transform: position.horizontal === 'center' ? 'translateX(-50%)' : 'none',
          width: 'calc(100vw - 32px)',
          maxWidth: '400px',
        },
        ...style,
      }}
      ClickAwayListenerProps={{
        onClickAway: onClose,
      }}
      className={className}
    >
      <Paper
        elevation={8}
        sx={{
          backgroundColor: getBackgroundColor(),
          color: customColors.text,
          borderRadius: '12px',
          overflow: 'hidden',
          maxWidth: { xs: '100%', sm: '400px' },
          minWidth: { xs: '280px', sm: '320px' },
          width: { xs: '100%', sm: 'auto' },
          cursor: clickable && notification ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
          '&:hover':
            clickable && notification
              ? {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 25px ${getBackgroundColor()}40`,
                }
              : {},
          '@media (max-width: 768px)': {
            maxWidth: '100%',
            minWidth: '280px',
            mx: 1,
          },
        }}
        onClick={handleClick}
      >
        <Box sx={{ position: 'relative', p: { xs: 1.5, sm: 2 } }}>
          {showCloseButton && (
            <IconButton
              size="small"
              onClick={handleCloseClick}
              sx={{
                position: 'absolute',
                right: { xs: 4, sm: 8 },
                top: { xs: 4, sm: 8 },
                color: customColors.text,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: { xs: 1, sm: 1.5 },
              pr: showCloseButton ? { xs: 2.5, sm: 3 } : 0,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '50%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.5,
                flexShrink: 0,
              }}
            >
              {getTypeIcon()}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {notification ? (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '14px',
                      lineHeight: 1.3,
                      color: 'white',
                    }}
                  >
                    New Notification
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.95,
                      fontSize: '13px',
                      lineHeight: 1.4,
                      mb: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                      color: 'white',
                    }}
                  >
                    You have a new message!
                  </Typography>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 1.5,
                    }}
                  >
                    {clickable && onClick && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          opacity: 0.7,
                          fontStyle: 'italic',
                        }}
                      >
                        Click to view details
                      </Typography>
                    )}

                    {notification?.latestResponse?.createdAt && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '10px',
                          opacity: 0.6,
                          ml: 'auto',
                          marginRight: '-20px',
                        }}
                      >
                        {formatTimestamp(notification.latestResponse?.createdAt)}
                      </Typography>
                    )}
                  </Box>
                </>
              ) : (
                <>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '14px',
                      color: 'white',
                    }}
                  >
                    {noDataMessage.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      opacity: 0.9,
                      fontSize: '13px',
                      lineHeight: 1.4,
                      mb: 1,
                      color: 'white',
                    }}
                  >
                    {noDataMessage.subtitle}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '11px',
                      opacity: 0.7,
                      fontStyle: 'italic',
                    }}
                  >
                    We&apos;ll notify you when something new arrives
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Snackbar>
  );
};

NotificationComponent.defaultProps = {
  onClick: undefined,
  position: { vertical: 'top', horizontal: 'center' },
  autoHideDuration: null,
  showCloseButton: true,
  clickable: true,
  customColors: {
    background: '#0072b0',
    text: 'white',
    icon: 'white',
  },
  noDataMessage: {
    title: 'All Caught Up!',
    subtitle: "You don't have any new messages at the moment.",
  },
  className: undefined,
  style: undefined,
};

export default NotificationComponent;
